import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileCheck2, ArrowRight, Info } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function Visa() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pathways, setPathways] = useState([]);
  const [opening, setOpening] = useState("");

  useEffect(() => {
    api.get("/visa/pathways").then(({ data }) => setPathways(data)).catch(() => {});
  }, []);

  const openFile = async (key) => {
    if (!user) {
      toast.info("Connectez-vous pour ouvrir un dossier");
      navigate("/login");
      return;
    }
    if (user.role === "admin") {
      toast.info("Les dossiers s'ouvrent avec un compte client.");
      return;
    }
    setOpening(key);
    try {
      const { data } = await api.post("/visa/files", { pathway_key: key });
      toast.success("Dossier ouvert !");
      navigate(`/client/visa/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setOpening("");
    }
  };

  return (
    <div>
      <section className="relative h-64 flex items-end">
        <img src="https://images.unsplash.com/photo-1581553673739-c4906b5d0de8" alt="Passeport" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-6xl mx-auto px-4 pb-8 text-white">
          <h1 className="font-head text-4xl sm:text-5xl font-extrabold text-shadow-hero">Visa & Passeport</h1>
          <p className="mt-2 text-white/90 text-shadow-hero max-w-xl">Un accompagnement complet, étape par étape, jusqu'à votre départ.</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="flex items-start gap-3 bg-accent/10 border border-accent/30 rounded-2xl p-4 mb-8" data-testid="visa-indicative-notice">
          <Info className="h-5 w-5 text-accent shrink-0 mt-0.5" />
          <p className="text-sm text-primary/90">
            <strong>Liste indicative</strong> : l'agence confirme les pièces exactes selon votre situation lors de l'ouverture du dossier.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          {pathways.map((p) => (
            <div key={p.key} className="bg-white rounded-2xl soft-shadow border border-gray-100 p-6 card-hover" data-testid={`pathway-${p.key}`}>
              <h2 className="font-head text-2xl font-extrabold text-primary mb-2">{p.name}</h2>
              <p className="text-muted-foreground text-sm mb-4">{p.description}</p>
              <div className="text-sm font-semibold text-primary mb-2">Pièces à fournir (indicatif)</div>
              <ul className="space-y-1.5 mb-5">
                {p.documents.map((d, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-primary/80">
                    <FileCheck2 className="h-4 w-4 text-accent shrink-0" /> {d}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => openFile(p.key)}
                disabled={opening === p.key}
                className="w-full h-11 rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold"
                data-testid={`open-file-${p.key}`}
              >
                {opening === p.key ? "Ouverture..." : <>Ouvrir mon dossier <ArrowRight className="ml-2 h-4 w-4" /></>}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
